import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2, RefreshCw, AlertCircle, Mic, Square } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PulseGPTChat = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const finalTranscriptRef = useRef('');

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SpeechRecognition);
    
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      
      // Keep listening continuously until user stops
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;
      
      recognition.onresult = (event) => {
        let interim = '';
        let final = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript + ' ';
          } else {
            interim += transcript;
          }
        }
        
        // Accumulate final transcript
        if (final) {
          finalTranscriptRef.current += final;
          setInput(finalTranscriptRef.current.trim());
        }
        
        // Show interim results
        setInterimTranscript(interim);
        
        // Reset silence timeout on any speech detected
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
        
        // Set a longer silence timeout (5 seconds) before auto-stopping
        silenceTimeoutRef.current = setTimeout(() => {
          if (recognitionRef.current && isListening) {
            // Don't auto-stop, just let user manually stop
            // This gives them control over when they're done
          }
        }, 5000);
      };
      
      recognition.onend = () => {
        // If still supposed to be listening, restart (handles browser auto-stop)
        if (isListening && recognitionRef.current) {
          try {
            recognition.start();
          } catch (e) {
            // Already started or other error
            setIsListening(false);
          }
        } else {
          setIsListening(false);
        }
        setInterimTranscript('');
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        // Don't stop on no-speech error, keep listening
        if (event.error === 'no-speech') {
          // Restart if still listening
          if (isListening) {
            try {
              recognition.start();
            } catch (e) {
              // Ignore
            }
          }
          return;
        }
        
        if (event.error === 'not-allowed') {
          setError('Microphone access denied. Please enable microphone permissions.');
          setIsListening(false);
        } else if (event.error === 'aborted') {
          // User stopped, that's fine
          setIsListening(false);
        } else {
          setIsListening(false);
        }
      };
      
      recognition.onspeechend = () => {
        // Don't stop on speech end, user controls when done
      };
      
      recognitionRef.current = recognition;
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, [isListening]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Hey — I'm **PH Pulse GPT**.
I'm here to help you understand how things are actually performing.

You can ask me things like:

- **"How are our Q4 KPIs really looking?"**
- **"What's changed in Marketing this month compared to last?"**
- **"Which KPIs are off track right now?"**
- **"Has Finance uploaded their weekly numbers yet?"**

I only work with verified data inside Pulse — no guesses, no inflated numbers, no spin.
The goal is simple: clear answers, shared accountability, and decisions rooted in truth.

What would you like to look at?`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, messages.length]);

  const toggleListening = useCallback(() => {
    if (!speechSupported || !recognitionRef.current) return;
    
    if (isListening) {
      // Stop listening
      recognitionRef.current.stop();
      setIsListening(false);
      setInterimTranscript('');
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    } else {
      // Start listening
      setError(null);
      finalTranscriptRef.current = input; // Keep existing text
      setInterimTranscript('');
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error('Failed to start speech recognition:', err);
        setError('Failed to start voice input. Please try again.');
      }
    }
  }, [isListening, speechSupported, input]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    // Stop listening if active
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      setInterimTranscript('');
    }

    const userMessage = input.trim();
    setInput('');
    finalTranscriptRef.current = '';
    setError(null);

    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/pulse-gpt/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_history: conversationHistory
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to get response');
      }

      const data = await response.json();

      // Add assistant response
      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        tools_used: data.tools_used,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setConversationHistory(data.conversation_history);

    } catch (err) {
      console.error('Pulse GPT error:', err);
      setError(err.message || 'Failed to communicate with Pulse GPT');
      // Add error message to chat
      setMessages(prev => [...prev, {
        role: 'error',
        content: err.message || 'Something went wrong. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: `Chat cleared! What would you like to look at?`,
      timestamp: new Date().toISOString()
    }]);
    setConversationHistory([]);
    setError(null);
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, i) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Bullet points
        if (line.startsWith('- ')) {
          return `<li key="${i}" class="ml-4">${line.slice(2)}</li>`;
        }
        // Dashboard links
        line = line.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#2563EB] hover:underline">$1</a>');
        return line;
      })
      .join('<br />');
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed bottom-24 right-6 w-[420px] h-[600px] bg-white rounded-xl shadow-2xl border border-[#E5E7EB] flex flex-col z-50 animate-fade-in"
      data-testid="pulse-gpt-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] rounded-t-xl">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center overflow-hidden">
            <img 
              src="https://customer-assets.emergentagent.com/job_9c85cef9-8e5b-4af9-b5f8-e3b78d6826aa/artifacts/fve2vy6a_players-health-logo-color%20%285%29.png" 
              alt="PH" 
              className="w-7 h-7 object-contain"
            />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">PH Pulse GPT</h3>
            <p className="text-white/70 text-xs">Verified Data Insights</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearChat}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title="Clear chat"
            data-testid="clear-chat-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="close-chat-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-[#2563EB] text-white'
                  : msg.role === 'error'
                  ? 'bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B]'
                  : 'bg-[#F3F4F6] text-[#111827]'
              }`}
            >
              {msg.role === 'error' && (
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium text-sm">Error</span>
                </div>
              )}
              <div 
                className="text-sm leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
              />
              {msg.tools_used && msg.tools_used.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[#D1D5DB]/50">
                  <span className="text-xs text-[#6B7280]">
                    📊 Analyzed: {msg.tools_used.join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-[#F3F4F6] rounded-xl px-4 py-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#2563EB]" />
              <span className="text-sm text-[#6B7280]">Analyzing data...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-[#E5E7EB]">
        {error && (
          <div className="mb-2 text-xs text-[#991B1B] bg-[#FEF2F2] px-3 py-2 rounded-lg">
            {error}
          </div>
        )}
        
        {/* Listening indicator with live transcription preview */}
        {isListening && (
          <div className="mb-2 bg-[#EFF6FF] border border-[#BFDBFE] px-3 py-2 rounded-lg">
            <div className="flex items-center gap-2 text-xs text-[#2563EB] mb-1">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#DC2626] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#DC2626]"></span>
              </span>
              <span className="font-medium">Recording — Click stop when done</span>
            </div>
            {interimTranscript && (
              <p className="text-xs text-[#6B7280] italic mt-1">
                {interimTranscript}...
              </p>
            )}
          </div>
        )}
        
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              finalTranscriptRef.current = e.target.value;
            }}
            onKeyDown={handleKeyDown}
            placeholder={isListening ? "Speak your question..." : "Ask about KPIs, trends, performance..."}
            className={`flex-1 resize-none rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] min-h-[48px] max-h-[120px] ${
              isListening ? 'border-[#2563EB] bg-[#F8FAFC]' : 'border-[#E5E7EB]'
            }`}
            rows={1}
            data-testid="pulse-gpt-input"
          />
          
          {/* Voice Input Button */}
          {speechSupported && (
            <button
              onClick={toggleListening}
              disabled={isLoading}
              className={`h-12 w-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                isListening 
                  ? 'bg-[#DC2626] text-white hover:bg-[#B91C1C] shadow-lg shadow-red-200' 
                  : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#111827]'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="voice-input-btn"
              title={isListening ? "Stop recording" : "Start voice input"}
            >
              {isListening ? (
                <Square className="w-5 h-5" fill="currentColor" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
            </button>
          )}
          
          {/* Send Button */}
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className="h-12 w-12 rounded-xl bg-[#2563EB] text-white flex items-center justify-center hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="send-message-btn"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-[10px] text-[#9CA3AF] mt-2 text-center">
          PH Pulse GPT works with verified data only. No guesses, no spin.
        </p>
      </div>
    </div>
  );
};

export default PulseGPTChat;
